import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { personLd, breadcrumbLd } from "../lib/seo";

export default function GuidePage() {
  const { t, lang } = useLang();
  const site = useSite();
  const g = site?.settings?.guide || {};
  const c = site?.settings?.contacts || {};

  const sections = [
    { h: lang === "ru" ? "Кто я" : "Who I am", p: lang === "ru" ? g.story_ru : g.story_en },
    { h: lang === "ru" ? "Почему Алтай" : "Why Altai", p: lang === "ru" ? "Алтай нельзя увидеть за один день. Я показываю его так, как знаю сам(а) — через тишину перевалов, свет над степью и разговоры у реки." : "Altai cannot be seen in a day. I show it the way I know it — through the silence of passes, light over the steppe and conversations by the river." },
    { h: lang === "ru" ? "Как проходят экскурсии" : "How excursions run", p: lang === "ru" ? "Маленькие группы, гибкий ритм, остановки для фотографий. Маршрут адаптируется под погоду, дорогу и сезон." : "Small groups, a flexible rhythm, photography stops. Routes adapt to weather, road and season." },
    { h: lang === "ru" ? "Принципы АРГАЛИ" : "АРГАЛИ principles", p: lang === "ru" ? "Уважение к священным местам, к природе и к местным традициям. Мы не оставляем следов и следуем правилам ООПТ." : "Respect for sacred places, nature and local traditions. We leave no trace and follow protected-area rules." },
  ];

  return (
    <div data-testid="guide-page" style={{ background: "#342116" }}>
      <Seo
        path="/guide"
        lang={lang}
        title={lang === "ru" ? "О гиде" : "The guide"}
        description={lang === "ru" ? `${g.name_ru || "Гид АРГАЛИ"} — аттестованный экскурсовод Республики Алтай. Индивидуальные путешествия по Алтаю.` : `${g.name_en || "АРГАЛИ guide"} — attested guide of the Altai Republic. Private journeys across Altai.`}
        jsonLd={[g.name_ru ? personLd(g, lang) : null, breadcrumbLd([{ name: "АРГАЛИ", path: "/" }, { name: lang === "ru" ? "О гиде" : "The guide", path: "/guide" }])]}
      />
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-36 pb-16 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden bg-argali-obsidian">
            {g.portrait_url ? (
              <img src={g.portrait_url} alt={g.name_ru} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
                <img src="/brand/argali_emblem_ru.png" alt="Логотип АРГАЛИ" width="543" height="501" className="w-24 h-auto mb-4 opacity-90" />
                <p className="text-argali-sandstone/60 text-sm uppercase tracking-widest">{lang === "ru" ? "Портрет гида" : "Guide portrait"}</p>
                <p className="text-argali-sandstone/30 text-xs mt-2">{lang === "ru" ? "(добавляется в админ-панели)" : "(set in admin panel)"}</p>
              </div>
            )}
            <div className="absolute top-4 left-4 text-[0.6rem] uppercase tracking-[0.16em] bg-argali-bronze text-argali-white px-3 py-1.5">{t("attested")}</div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow">{lang === "ru" ? "О гиде" : "The guide"}</p>
          <h1 className="display display-md text-argali-ivory mt-3">{lang === "ru" ? g.name_ru : g.name_en}</h1>
          <div className="mt-6 space-y-2 text-sm text-argali-sandstone/80">
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Аттестация" : "Attestation"}:</span> {g.experience_ru}</p>
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Номер в реестре" : "Register number"}:</span> {g.guide_number}</p>
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Языки" : "Languages"}:</span> {g.languages_ru}</p>
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Регионы" : "Regions"}:</span> {g.regions_ru}</p>
          </div>
          <Link to="/guide/stories" className="btn-outline group mt-7 inline-flex items-center gap-2"><Camera size={16} /> {lang === "ru" ? "Истории и фотографии" : "Stories and photos"} <ArrowRight size={15} className="btn-arrow" /></Link>
        </Reveal>
      </section>

      <section className="px-5 md:px-10 pb-32">
        <div className="max-w-3xl mx-auto space-y-12">
          {sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="border-t border-argali-bronze/30 pt-6">
                <h2 className="font-serif text-3xl text-argali-ivory">{s.h}</h2>
                <p className="text-argali-ivory/75 mt-4 leading-relaxed">{s.p}</p>
              </div>
            </Reveal>
          ))}
          <Reveal>
            <div className="border-t border-argali-bronze/30 pt-6">
              <h2 className="font-serif text-3xl text-argali-ivory">{t("nav_contacts")}</h2>
              <div className="flex flex-wrap gap-4 mt-5">
                {c.phone && <a href={`tel:${c.phone}`} className="btn-outline">{c.phone}</a>}
                {c.email && <a href={`mailto:${c.email}`} className="btn-outline">{c.email}</a>}
              </div>
              <Link to="/excursions" className="btn-bronze group mt-8">{t("cta_choose_route")} <ArrowRight size={16} className="btn-arrow" /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}